import {
  DocumentNode,
  NetworkStatus,
  gql,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client';
import { useBoolean, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EditableCard } from '@/components/EditableCard';
import { mergeGroupSettingAttribute } from '@/components/group-form/groupAttributeUtils';
import { GroupDetailPageLayout_GroupQuery_group_result_parentGroup } from '@/components/admin/groups/graphql-types/GroupDetailPageLayout_GroupQuery';
import {
  ContactAttributeInput,
  EGroupConditionLogic,
  EGroupFindPermission,
  EGroupSelfJoinPermission,
  EGroupViewContentPermission,
  UpdateGroupSettingsDataInput,
} from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { useValidFindPermissionOptions } from '@/hooks/useValidFindPermissionOptions';

import { SettingsCard_GroupFragment } from './graphql-types/SettingsCard_GroupFragment';
import {
  SettingsCard_PreviewGroupAccessPolicyChange,
  SettingsCard_PreviewGroupAccessPolicyChangeVariables,
} from './graphql-types/SettingsCard_PreviewGroupAccessPolicyChange';
import {
  SettingsCard_UpdateGroup,
  SettingsCard_UpdateGroupVariables,
} from './graphql-types/SettingsCard_UpdateGroup';
import { PolicyChangePreviewModal } from '../PolicyChangePreviewModal/PolicyChangePreviewModal';

import { SettingsForm } from '../../../SettingsForm';

const PREVIEW_POLICY_CHANGE = gql`
  query SettingsCard_PreviewGroupAccessPolicyChange(
    $input: PreviewGroupAccessPolicyChangeInput!
  ) {
    previewGroupAccessPolicyChange(input: $input) {
      suspendCount
      restoreCount
      membersToSuspend {
        userId
        displayName
        userGroupRole
        subgroupNames
      }
      membersToRestore {
        userId
        displayName
        userGroupRole
        subgroupNames
      }
    }
  }
`;

const UPDATE_GROUP = gql`
  mutation SettingsCard_UpdateGroup(
    $input: UpdateGroupInput!
    $subgroupsInput: SubgroupsInput
  ) {
    updateGroup(input: $input) {
      data {
        _id
        settings {
          findPermission
          selfJoinPermission
          viewContentPermission
          peopleAttributes {
            customAttributes {
              schemaId
              values
              schemaName
              displayValues
              schemaType
              options {
                id
                label
              }
            }
            defaultAttributes {
              name
              values
              schemaName
              displayValues
              schemaType
              options {
                id
                label
              }
            }
          }
          companyAttributes {
            customAttributes {
              schemaId
              values
              schemaName
              displayValues
              schemaType
              options {
                id
                label
              }
            }
            defaultAttributes {
              name
              values
              schemaName
              displayValues
              schemaType
              options {
                id
                label
              }
            }
          }
          membershipTypeIds
          conditionLogic
          constantContactListId
        }
        parentGroup {
          _id
          subgroups(input: $subgroupsInput) {
            groupNodes {
              group {
                _id
                settings {
                  findPermission
                  selfJoinPermission
                  viewContentPermission
                }
              }
            }
          }
        }
      }
    }
  }
`;
const TAGS = gql`
  query AttributeSchemaList_And_MembershipTypes(
    $userInput: AttributeSchemasInput!
    $orgInput: AttributeSchemasInput!
    $membershipInput: MembershipTypesInput!
  ) {
    userAttributes: attributeSchemas(input: $userInput) {
      results {
        _id
        isDefault
        ...AttributeSchema_AttributeSchema
        __typename
      }
      __typename
    }
    orgAttributes: attributeSchemas(input: $orgInput) {
      results {
        _id
        isDefault
        ...AttributeSchema_AttributeSchema
        __typename
      }
      __typename
    }
    membershipTypes(input: $membershipInput) {
      ... on MembershipTypesSuccess {
        results {
          name
          _id
        }
      }
    }
  }

  fragment AttributeSchema_AttributeSchema on AttributeSchema {
    _id
    name
    isSearchable

    ... on SingleSelectAttributeSchema {
      options {
        id
        label
        __typename
      }
      __typename
    }
    ... on MultiSelectAttributeSchema {
      options {
        id
        label
        __typename
      }
      __typename
    }
    __typename
  }
`;
const GET_CC_STATUS = gql`
  query SettingsCard_GetCCStatus {
    emailMarketingIntegrationConstantContactGetStatus {
      status
    }
  }
`;

type ISettingsCardForm = {
  canFindConfig: EGroupFindPermission;
  canJoinConfig?: EGroupSelfJoinPermission;
  canViewContentConfig?: EGroupViewContentPermission;
  peopleAttributes?: ContactAttributeInput;
  companyAttributes?: ContactAttributeInput;
  membershipTypeIds?: GroupDetailPageLayout_GroupQuery_group_result_parentGroup[];
  configSelection?: string[];
  conditionLogic?: EGroupConditionLogic;
  additionalFields?: {
    values?: string[];
    [key: string]: any;
  }[];
  syncGroupWithCC?: boolean;
};

// TODO: create new interface for group in which extend all the existing group types and add isGlobalSingleton field
interface ExtendedGroup extends SettingsCard_GroupFragment {
  isGlobalSingleton: boolean;
}

export const SettingsCard: React.FC<{
  group: ExtendedGroup;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const confirmDialog = useDisclosure();
  const policyPreviewDialog = useDisclosure();
  const [isEditing, setIsEditing] = useBoolean();
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const permissions = usePermissions();
  const toast = useToast();
  const apolloClient = useApolloClient();
  const pendingExcludedUserIdsRef = React.useRef<string[]>([]);
  const pendingJoinDecisionRef = React.useRef<((accepted: boolean) => void) | null>(
    null
  );
  const [policyPreview, setPolicyPreview] = React.useState<{
    suspendCount: number;
    restoreCount: number;
    membersToSuspend: {
      userId: string;
      displayName?: string | null;
      userGroupRole?: string | null;
      subgroupNames?: string[] | null;
    }[];
    membersToRestore: {
      userId: string;
      displayName?: string | null;
      userGroupRole?: string | null;
      subgroupNames?: string[] | null;
    }[];
  }>({
    suspendCount: 0,
    restoreCount: 0,
    membersToSuspend: [],
    membersToRestore: [],
  });

  const [updateGroup, { loading }] = useMutation<
    SettingsCard_UpdateGroup,
    SettingsCard_UpdateGroupVariables
  >(UPDATE_GROUP, {
    onCompleted: () => {
      toast({
        title:
          'Your group settings have been updated. Please allow up to 1 hour for the changes to take full effect.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: 'Could not save group settings. Please try again.',
        status: 'error',
      });
    },
  });

  const { data: ccData } = useQuery(GET_CC_STATUS);

  const {
    validFindPermissionOptions,
    loading: validFindPermissionOptionsLoading,
  } = useValidFindPermissionOptions({ parentGroupId: group.parentGroup?._id });

  const {
    data,
    loading: attrLoading,
    error,
    networkStatus,
    previousData,
  } = useQuery(TAGS, {
    skip: !isEditing,
    variables: {
      userInput: {
        limit: 2000,
        offset: 0,
        filter: {
          ownerKind: 'USER',
        },
      },
      orgInput: {
        limit: 2000,
        offset: 0,
        filter: {
          ownerKind: 'ORGANIZATION',
        },
      },
      membershipInput: {
        limit: 2000,
        offset: 0,
        filter: {},
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const { settings, isGlobalSingleton } = group;

  const defaultValues = React.useMemo<ISettingsCardForm>(() => {
    const {
      membershipTypes = { results: [] },
      orgAttributes = { results: [] },
      userAttributes = { results: [] },
    } = data ?? {};
    if (validFindPermissionOptionsLoading || !settings.findPermission) {
      return {
        canFindConfig: EGroupFindPermission.GROUP_MEMBERS_ONLY,
        syncGroupWithCC: false,
        isGlobalSingleton,
      };
    }

    const peopleCustomAttributes =
      settings?.peopleAttributes?.customAttributes?.map((ele) =>
        mergeGroupSettingAttribute(
          ele ?? {},
          userAttributes?.results?.find(
            (obj: { _id: string }) => obj?._id === ele?.schemaId,
          ),
          { isDefault: false },
        ),
      ) || [];

    const peopleDefaultAttributes =
      settings?.peopleAttributes?.defaultAttributes?.map((ele) =>
        mergeGroupSettingAttribute(
          ele ?? {},
          userAttributes?.results?.find(
            (obj: { _id: string; name: string }) =>
              obj?._id === ele?.name || obj?.name === ele?.name,
          ),
          { isDefault: true },
        ),
      ) || [];

    const companyCustomAttributes =
      settings?.companyAttributes?.customAttributes?.map((ele) =>
        mergeGroupSettingAttribute(
          ele ?? {},
          orgAttributes?.results?.find(
            (obj: { _id: string }) => obj?._id === ele?.schemaId,
          ),
          { isDefault: false },
        ),
      ) || [];

    const companyDefaultAttributes =
      settings?.companyAttributes?.defaultAttributes?.map((ele) =>
        mergeGroupSettingAttribute(
          ele ?? {},
          orgAttributes?.results?.find(
            (obj: { _id: string; name: string }) =>
              obj?._id === ele?.name || obj?.name === ele?.name,
          ),
          { isDefault: true },
        ),
      ) || [];

    const membershipTypeIds =
      settings?.membershipTypeIds?.map((ele) => {
        const currentAttr = membershipTypes?.results?.find(
          (obj: any) => obj?._id === ele
        );
        return { ...currentAttr };
      }) || [];

    const findConfig =
      settings.findPermission &&
      validFindPermissionOptions.has(settings.findPermission)
        ? settings.findPermission
        : Array.from(validFindPermissionOptions.keys())[0];
    const joinConfig =
      validFindPermissionOptions.get(findConfig)?.selfJoinPermissions;
    const viewContentConfig =
      validFindPermissionOptions.get(findConfig)?.viewContentPermissions;
    const configSelection = [];
    if (
      settings?.peopleAttributes?.defaultAttributes?.length ||
      settings?.peopleAttributes?.customAttributes?.length
    ) {
      configSelection.push('people_attributes');
    }
    if (
      settings?.companyAttributes?.defaultAttributes?.length ||
      settings?.companyAttributes?.customAttributes?.length
    ) {
      configSelection.push('company_attributes');
    }
    if (settings?.membershipTypeIds?.length) {
      configSelection.push('membership_type');
    }

    return {
      canFindConfig: findConfig,
      canJoinConfig:
        settings.selfJoinPermission &&
        joinConfig &&
        joinConfig.includes(settings.selfJoinPermission)
          ? settings.selfJoinPermission
          : joinConfig?.[0],
      canViewContentConfig:
        settings.viewContentPermission &&
        viewContentConfig &&
        viewContentConfig.includes(settings.viewContentPermission)
          ? settings.viewContentPermission
          : viewContentConfig?.[0],
      peopleAttributes: {
        customAttributes: peopleCustomAttributes,
        defaultAttributes: peopleDefaultAttributes,
      },
      companyAttributes: {
        customAttributes: companyCustomAttributes,
        defaultAttributes: companyDefaultAttributes,
      },
      membershipTypeIds,
      configSelection,
      conditionLogic:
        settings?.conditionLogic || EGroupConditionLogic.OR,
      syncGroupWithCC: !!settings.constantContactListId,
      isGlobalSingleton,
    };
  }, [
    validFindPermissionOptionsLoading,
    settings,
    validFindPermissionOptions,
    data,
    isGlobalSingleton,
  ]);

  const methods = useForm<ISettingsCardForm>({ defaultValues });
  const { handleSubmit, reset, getValues } = methods;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const settleJoinDecision = React.useCallback(
    (accepted: boolean) => {
      pendingJoinDecisionRef.current?.(accepted);
      pendingJoinDecisionRef.current = null;
      policyPreviewDialog.onClose();
    },
    [policyPreviewDialog]
  );

  const previewAndConfirmPolicyChange = React.useCallback(
    async (proposed: {
      findPermission?: EGroupFindPermission;
      selfJoinPermission?: EGroupSelfJoinPermission;
      viewContentPermission?: EGroupViewContentPermission;
    }): Promise<boolean> => {
      setIsPreviewing(true);
      try {
        const previewResult = await apolloClient.query<
          SettingsCard_PreviewGroupAccessPolicyChange,
          SettingsCard_PreviewGroupAccessPolicyChangeVariables
        >({
          query: PREVIEW_POLICY_CHANGE,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              groupId: group._id,
              settings: {
                findPermission: proposed.findPermission,
                selfJoinPermission: proposed.selfJoinPermission,
                viewContentPermission: proposed.viewContentPermission,
                peopleAttributes: {
                  customAttributes: [],
                  defaultAttributes: [],
                },
                companyAttributes: {
                  customAttributes: [],
                  defaultAttributes: [],
                },
                membershipTypeIds: [],
              },
            },
          },
        });

        const preview = previewResult.data?.previewGroupAccessPolicyChange;
        setPolicyPreview({
          suspendCount: preview?.suspendCount || 0,
          restoreCount: preview?.restoreCount || 0,
          membersToSuspend: preview?.membersToSuspend || [],
          membersToRestore: preview?.membersToRestore || [],
        });
      } catch {
        toast({
          title: 'Could not preview this setting change. Please try again.',
          status: 'error',
        });
        return false;
      } finally {
        setIsPreviewing(false);
      }

      return await new Promise<boolean>((resolve) => {
        pendingJoinDecisionRef.current = resolve;
        policyPreviewDialog.onOpen();
      });
    },
    [apolloClient, group._id, policyPreviewDialog, toast]
  );

  const handleWhoCanJoinChange = React.useCallback(
    async (newJoin: EGroupSelfJoinPermission): Promise<boolean> => {
      const values = getValues();
      if (newJoin === values.canJoinConfig) {
        return false;
      }

      return previewAndConfirmPolicyChange({
        findPermission: values.canFindConfig,
        selfJoinPermission: newJoin,
        viewContentPermission: values.canViewContentConfig,
      });
    },
    [getValues, previewAndConfirmPolicyChange]
  );

  const handleWhoCanFindChange = React.useCallback(
    async (newFind: EGroupFindPermission): Promise<boolean> => {
      const values = getValues();
      if (newFind === values.canFindConfig) {
        return false;
      }

      const allowedJoin =
        validFindPermissionOptions.get(newFind)?.selfJoinPermissions ?? [];
      const proposedJoin =
        values.canJoinConfig && allowedJoin.includes(values.canJoinConfig)
          ? values.canJoinConfig
          : allowedJoin[0];
      const allowedView =
        validFindPermissionOptions.get(newFind)?.viewContentPermissions ?? [];
      const proposedView =
        values.canViewContentConfig &&
        allowedView.includes(values.canViewContentConfig)
          ? values.canViewContentConfig
          : allowedView[0];

      return previewAndConfirmPolicyChange({
        findPermission: newFind,
        selfJoinPermission: proposedJoin,
        viewContentPermission: proposedView,
      });
    },
    [getValues, previewAndConfirmPolicyChange, validFindPermissionOptions]
  );

  const submit = handleSubmit(async (data) => {
    const {
      canFindConfig,
      canJoinConfig,
      canViewContentConfig,
      syncGroupWithCC,
    } = data;

    const configSelection = data.configSelection || [];
    const peopleAttributes = data.peopleAttributes;
    const companyAttributes = data.companyAttributes;
    const membershipTypeList = data.membershipTypeIds;

    const additionalFields = [
      ...(peopleAttributes?.customAttributes || []),
      ...(peopleAttributes?.defaultAttributes || []),
      ...(companyAttributes?.customAttributes || []),
      ...(companyAttributes?.defaultAttributes || []),
    ];
    if (
      configSelection.includes('people_attributes') &&
      !peopleAttributes?.defaultAttributes?.length &&
      !peopleAttributes?.customAttributes?.length
    ) {
      return;
    }

    if (
      configSelection.includes('company_attributes') &&
      !companyAttributes?.defaultAttributes?.length &&
      !companyAttributes?.customAttributes?.length
    ) {
      return;
    }

    if (
      configSelection.includes('membership_type') &&
      !membershipTypeList?.length
    ) {
      return;
    }

    const hasEmptyAdditionalField = additionalFields.some((field) => {
      return !field?.values || field?.values.length === 0;
    });

    if (hasEmptyAdditionalField) {
      return;
    }

    const membershipTypeIds = membershipTypeList
      ?.map((ele) => ele?._id)
      .filter((id): id is string => Boolean(id));

    await persistSettings({
      findPermission: canFindConfig,
      selfJoinPermission: canJoinConfig,
      viewContentPermission: canViewContentConfig,
      peopleAttributes: {
        customAttributes: data?.peopleAttributes?.customAttributes?.map(
          (attribute: { schemaId: string; values: string[] }) => ({
            schemaId: attribute.schemaId,
            values: attribute.values,
          })
        ),
        defaultAttributes: data?.peopleAttributes?.defaultAttributes?.map(
          (attribute: { _id: string; values: string[] }) => ({
            name: attribute._id,
            values: attribute.values,
          })
        ),
      },
      companyAttributes: {
        customAttributes: data?.companyAttributes?.customAttributes?.map(
          (attribute: { schemaId: string; values: string[] }) => ({
            schemaId: attribute.schemaId,
            values: attribute.values,
          })
        ),
        defaultAttributes: data?.companyAttributes?.defaultAttributes?.map(
          (attribute: { _id: string; values: string[] }) => ({
            name: attribute._id,
            values: attribute.values,
          })
        ),
      },
      membershipTypeIds,
      conditionLogic: data.conditionLogic || EGroupConditionLogic.OR,
      syncGroupWithCC,
    });
  });

  async function persistSettings(settingsPayload: UpdateGroupSettingsDataInput) {
    await updateGroup({
      variables: {
        input: {
          id: group._id,
          excludedUserIds: pendingExcludedUserIdsRef.current,
          data: {
            settings: settingsPayload,
          },
        },
        subgroupsInput: {
          filter: permissions.canDoAdminStuffAndAuthenticated
            ? { includeAll: true }
            : undefined,
        },
      },
    });
    pendingExcludedUserIdsRef.current = [];
    setIsEditing.off();
    policyPreviewDialog.onClose();
  }

  return (
    <FormProvider {...methods}>
      <EditableCard
        title={
          !!group.parentGroup?._id ? 'Subgroup Settings' : 'Group Settings'
        }
        titleTooltip={
          !!group.parentGroup?._id
            ? "A subgroup's settings cannot be less strict than its parent group settings."
            : undefined
        }
        onSubmit={
          group.subgroups.totalCount > 0 ? confirmDialog.onOpen : submit
        }
        onClickEdit={setIsEditing.on}
        onClickCancel={() => {
          settleJoinDecision(false);
          pendingExcludedUserIdsRef.current = [];
          setIsEditing.off();
          reset();
        }}
        isEditing={isEditing}
        isCancelDisabled={loading || isPreviewing || policyPreviewDialog.isOpen}
        isSubmitLoading={
          loading || isPreviewing || policyPreviewDialog.isOpen
        }
        w="full"
      >
        <ConfirmDialog
          title="These changes may affect your subgroups"
          body={`You may currently have subgroups that have less strict permissions than the ones you've selected for the parent. Confirming these changes will update those subgroup permissions to match the parent group.`}
          onConfirm={submit}
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          confirmButtonText="Confirm"
        />
        <PolicyChangePreviewModal
          isOpen={policyPreviewDialog.isOpen}
          onClose={() => settleJoinDecision(false)}
          suspendCount={policyPreview.suspendCount}
          restoreCount={policyPreview.restoreCount}
          membersToSuspend={policyPreview.membersToSuspend}
          membersToRestore={policyPreview.membersToRestore}
          onConfirm={(excludedUserIds) => {
            pendingExcludedUserIdsRef.current = excludedUserIds;
            settleJoinDecision(true);
          }}
        />
        <SettingsForm
          isEditing={isEditing}
          parentGroupId={group.parentGroup?._id}
          onRequestJoinChange={handleWhoCanJoinChange}
          onRequestFindChange={handleWhoCanFindChange}
          isJoinChangeLoading={isPreviewing || policyPreviewDialog.isOpen}
          isFindChangeLoading={isPreviewing || policyPreviewDialog.isOpen}
        />
      </EditableCard>
    </FormProvider>
  );
};
SettingsCard.groupFragment = gql`
  fragment SettingsCard_GroupFragment on Group {
    _id
    isGlobalSingleton
    settings {
      findPermission
      selfJoinPermission
      viewContentPermission
      peopleAttributes {
        customAttributes {
          schemaId
          values
          schemaName
          displayValues
          schemaType
          options {
            id
            label
          }
        }
        defaultAttributes {
          name
          values
          schemaName
          displayValues
          schemaType
          options {
            id
            label
          }
        }
      }
      companyAttributes {
        customAttributes {
          schemaId
          values
          schemaName
          displayValues
          schemaType
          options {
            id
            label
          }
        }
        defaultAttributes {
          name
          values
          schemaName
          displayValues
          schemaType
          options {
            id
            label
          }
        }
      }
      membershipTypeIds
      conditionLogic
      constantContactListId
    }
    parentGroup {
      _id
    }
    subgroups(input: $subgroupsInput) {
      totalCount
    }
  }
`;
